import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';

import { DashboardDataProvider, useDashboardData } from '@/contexts/DashboardDataContext';
import { useStatistiquesFilters } from '@/hooks/useStatistiquesFilters';
import { useLayout } from '@/contexts/LayoutContext';
import { useStatistics } from '@/hooks/useStatistics';
import { useRequiredActions } from '@/hooks/useRequiredActions';

import { StatCard } from '@/components/StatCard';
import { PerformanceChart } from '@/components/PerformanceChart';
import { ActionItem } from '@/components/ActionItem';


import {
    ChartIcon,
    ChartRedIcon,
    ActivityIcon,
    TimeIcon,
    ProfileIcon,
    TimerIcon,
    ShareIcon,
    RatingIcon
} from '@/components/FigmaIcons';
import { CustomPeriodFilter } from '@/components/filters/CustomPeriodFilter';
import { PeriodFilter } from '@/components/filters/PeriodFilter';
import { NotificationDropdown } from '@/components/layout/NotificationDropdown';

/**
 * ✅ PHASE 1B : Composant interne qui consomme le context
 */
const StatistiquesContent = () => {
    const navigate = useNavigate();
    const { setCurrentLayout, setSidebarVisible } = useLayout();
    
    // ✅ PHASE 1B: Consommer depuis le context au lieu d'appeler le hook directement
    const {
        notifications,
        unreadCount,
        markNotificationAsRead,
        markAllNotificationsAsRead
    } = useDashboardData();
    
    const {
        filters,
        updateCustomPeriod,
        updatePeriod
    } = useStatistiquesFilters();

    // Charger les statistiques réelles
    const { stats, loading, error } = useStatistics();
    
    // Charger les actions requises (React Query)
    const { data: requiredActionsData } = useRequiredActions();
    
    const overdueCampaigns = requiredActionsData?.overdueCampaigns || 0;
    const fraudAlerts = requiredActionsData?.fraudAlerts || 0;
    const unsharedReports = requiredActionsData?.unsharedReports || 0;

    // Forcer le layout avec sidebar pour cette page
    useEffect(() => {
        setCurrentLayout('default');
        setSidebarVisible(true);
    }, [setCurrentLayout, setSidebarVisible]);

    // Debug des filtres
    useEffect(() => {
        // console.log('Filtres mis à jour:', filters);
    }, [filters]);

    // Données des métriques avec couleurs exactes du design system
    const metricsData = [
        {
            title: "campagnes en cours",
            value: stats?.nombre_campagnes?.toString() || "0",
            trend: stats?.nombre_campagnes_trend || 0,
            icon: <ChartIcon />,
            iconBg: "bg-teal-400/10" // Exacte du Figma
        },
        {
            title: "campagnes en attente",
            value: stats?.campagnes_en_attente?.toString() || "0",
            trend: stats?.campagnes_en_attente_trend || 0,
            icon: <ChartRedIcon />,
            iconBg: "bg-rose-500/10" // Exacte du Figma
        },
        {
            title: "Taux de participation",
            value: `${stats?.taux_reponse || 0}%`,
            trend: stats?.taux_reponse_trend || 0,
            icon: <ActivityIcon />,
            iconBg: "bg-purple-600/10" // Exacte du Figma
        },
        {
            title: "Temps gagné",
            value: `${stats?.temps_gagne_minutes || 0} min`,
            trend: stats?.temps_gagne_trend || 0,
            icon: <TimeIcon />,
            iconBg: "bg-orange-400/10" // Exacte du Figma
        },
        {
            title: "Délai moyen",
            value: `${stats?.delai_moyen_jours || 0}j`,
            trend: stats?.delai_moyen_trend || 0,
            icon: <TimerIcon />,
            iconBg: "bg-green-500/10" // Exacte du Figma
        },
        {
            title: "Nombre de relances",
            value: stats?.nombre_relances?.toString() || "0",
            trend: stats?.nombre_relances_trend || 0,
            icon: <ShareIcon />,
            iconBg: "bg-indigo-500/10" // Exacte du Figma
        },
        {
            title: "Référents contactés",
            value: stats?.referents_contactes?.toString() || "0",
            trend: stats?.referents_contactes_trend || 0,
            icon: <ProfileIcon />,
            iconBg: "bg-sky-500/10" // Exacte du Figma
        },
        {
            title: "Note moyenne",
            value: stats?.note_moyenne?.toString() || "0",
            trend: stats?.note_moyenne_trend || 0,
            icon: <RatingIcon />,
            iconBg: "bg-yellow-400/10" // Exacte du Figma
        }
    ];

    // Actions requises avec vraies données
    const actionsRequises = [
        {
            title: "Relancer les campagnes hors délais",
            count: overdueCampaigns,
            onClick: () => navigate('/dashboard?filter=overdue')
        },
        {
            title: "Campagnes avec suspicion de fraude",
            count: fraudAlerts,
            onClick: () => navigate('/dashboard?filter=fraud')
        },
        {
            title: "Feedback candidat",
            count: unsharedReports,
            onClick: () => navigate('/reports?filter=not-shared')
        }
    ].filter(action => action.count > 0); // Ne montrer que les actions avec un compteur > 0

    // Gestion du loading
    if (loading) {
        return (
            <AppLayout>
                <div className="bg-white font-sans flex-1 min-h-screen">
                    <main className="flex-1 p-6">
                        <div className="flex items-center justify-center h-96">
                            <div className="text-gray-500">Chargement des statistiques...</div>
                        </div>
                    </main>
                </div>
            </AppLayout>
        );
    }

    // Gestion des erreurs
    if (error) {
        return (
            <AppLayout>
                <div className="bg-white font-sans flex-1 min-h-screen">
                    <main className="flex-1 p-6">
                        <div className="flex items-center justify-center h-96">
                            <div className="text-red-500">Erreur: {error.message}</div>
                        </div>
                    </main>
                </div>
            </AppLayout>
        );
    }

    return (
        <AppLayout>
            <div className="bg-white font-sans flex-1 min-h-screen">
                <main className="flex-1 p-6">
                    <div className="w-full flex flex-col gap-6 min-h-full">

                        {/* Header avec titre et notifications */}
                        <div className="flex items-center justify-between">
                            <h1 className="text-3xl font-semibold text-gray-900 leading-tight font-['Bricolage_Grotesque']">
                                Statistiques
                            </h1>

                            {/* Notification */}
                            <div className="inline-flex justify-start items-center gap-3">
                                <NotificationDropdown
                                    notifications={notifications}
                                    unreadCount={unreadCount}
                                    onMarkAsRead={markNotificationAsRead}
                                    onMarkAllAsRead={markAllNotificationsAsRead}
                                    hasUnread={unreadCount > 0}
                                />
                            </div>
                        </div>

                        {/* Filtres - Structure exacte Figma */}
                        <div className="flex justify-end items-center">
                            <div className="inline-flex justify-start items-center gap-3">
                                <CustomPeriodFilter
                                    onPeriodChange={updateCustomPeriod}
                                />
                                <PeriodFilter
                                    onPeriodChange={updatePeriod}
                                />
                            </div>
                        </div>

                        {/* Grille des métriques principales - Structure exacte Figma */}
                        <div className="flex flex-col gap-5">
                            {/* Première rangée - 4 cards */}
                            <div className="self-stretch inline-flex justify-start items-center gap-5">
                                {metricsData.slice(0, 4).map((metric, index) => (
                                    <StatCard
                                        key={index}
                                        icon={metric.icon}
                                        value={metric.value}
                                        label={metric.title}
                                        trend={metric.trend}
                                        iconBg={metric.iconBg}
                                    />
                                ))}
                            </div>

                            {/* Deuxième rangée - 4 cards */}
                            <div className="self-stretch inline-flex justify-start items-center gap-5">
                                {metricsData.slice(4, 8).map((metric, index) => (
                                    <StatCard
                                        key={index + 4}
                                        icon={metric.icon}
                                        value={metric.value}
                                        label={metric.title}
                                        trend={metric.trend}
                                        iconBg={metric.iconBg}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Graphique d'évolution des performances - Recharts complet */}
                        <PerformanceChart filters={filters} />

                        {/* Actions requises - Design system exact */}
                        {actionsRequises.length > 0 && (
                            <div className="bg-white border border-gray-200 rounded-xl p-6">
                                <h3 className="text-xl font-semibold text-gray-900 mb-4 font-['Bricolage_Grotesque']">
                                    Actions requises
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {actionsRequises.map((action, index) => (
                                        <ActionItem
                                            key={index}
                                            title={action.title}
                                            count={action.count}
                                            onClick={action.onClick}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}



                    </div>
                </main>
            </div>
        </AppLayout>
    );
};

/**
 * ✅ PHASE 1B : Wrapper avec DashboardDataProvider
 * 
 * Statistiques utilise AppLayout (pas AppLayoutWithHeader) donc doit wrapper lui-même
 * Le Provider centralise useDashboardWithPolling (1 seul appel)
 * et attend que userId + orgId + userRole soient définis avant de rendre
 */
const Statistiques = () => {
    return (
        <DashboardDataProvider>
            <StatistiquesContent />
        </DashboardDataProvider>
    );
};

export default Statistiques;